import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import waiverHtml from '../gym_app_liability_waiver';

const AuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [checkingWaiver, setCheckingWaiver] = useState(false);
  const { setAuthFromToken } = useAuth();

  console.log('AuthSuccess component rendered');
  console.log('Current URL:', window.location.href);

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        const name = searchParams.get('name');

        console.log('AuthSuccess - URL params:', { token, email, name });
        console.log('AuthSuccess - setAuthFromToken function:', setAuthFromToken);

        if (!token || !email) {
          console.error('Missing token or email:', { token, email });
          setError('Invalid authentication response');
          setLoading(false);
          return;
        }

        // Update auth context with the Google OAuth token
        setAuthFromToken(token, email);
        console.log('Auth context updated successfully');
        
        // Check waiver status
        try {
          const waiverResponse = await axios.get('/api/waiver/status');
          if (!waiverResponse.data.waiverAccepted) {
            setWaiverOpen(true);
            setLoading(false);
            return;
          }
        } catch (waiverError) {
          console.error('Error checking waiver status:', waiverError);
          // If error, still allow user to proceed but show waiver
          setWaiverOpen(true);
          setLoading(false);
          return;
        }
        
        setLoading(false);
        
        // Redirect to home page
        setTimeout(() => {
          navigate('/');
        }, 2000);

      } catch (err) {
        console.error('Auth success error:', err);
        setError('Failed to complete authentication');
        setLoading(false);
      }
    };

    handleAuthSuccess();
  }, [searchParams, navigate, setAuthFromToken]);

  const handleAcceptWaiver = async () => {
    if (!waiverAccepted) {
      return;
    }

    try {
      setCheckingWaiver(true);
      await axios.post('/api/waiver/accept');
      setWaiverOpen(false);
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err) {
      console.error('Error accepting waiver:', err);
      alert('Failed to accept waiver. Please try again.');
    } finally {
      setCheckingWaiver(false);
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body1">
            Please try signing in again.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {loading ? (
              <>
                <CircularProgress size={60} />
                <Typography variant="h6">Completing sign-in...</Typography>
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                <Typography variant="h5" color="success.main">
                  Sign-in Successful!
                </Typography>
                <Typography variant="body1">
                  Redirecting to your dashboard...
                </Typography>
              </>
            )}
          </Box>
        </Paper>
      </Container>
      
      <Dialog 
        open={waiverOpen} 
        onClose={() => {}} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Liability Waiver and Release Agreement</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 500, overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: waiverHtml }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={waiverAccepted}
                onChange={(e) => setWaiverAccepted(e.target.checked)}
                color="primary"
                required
              />
            }
            label="I have read, understood, and agree to this liability waiver."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleAcceptWaiver} 
            variant="contained"
            color="primary"
            disabled={!waiverAccepted || checkingWaiver}
          >
            {checkingWaiver ? 'Processing...' : 'I Agree'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuthSuccess;
